import { createSelector } from '@ngrx/store';
import { AccountTypes, FilteredAccount, IRATypes, RejectedAccount } from '@shared/models';
import { getConfigClientFromRTE } from '@src/app/core/config/config-client';
import { getIsGiftingFlow } from '@store/reducers/triage/triage.reducer';
import { selectSelectedPositions } from '@store/selectors/cost-basis/cost-basis.selectors';

import * as fromClientAccounts from '../../reducers/client-accounts/client-accounts.reducer';
import * as fromFeature from '../../reducers/index';
import {
  selectCashPlusInternalTransferToggle,
  selectInternalGiftingManagedAccountsToggle,
  selectToFromConsolidationToggle,
} from '../../selectors/feature/feature.selectors';
import {
  selectInternalTransferFrom,
  selectInternalTransferFromAccountType,
  selectInternalTransferToAccountType,
} from '../internal-transfer/internal-transfer.selectors';
import { selectIraType } from '../selected-client-accounts/selected-client-accounts.selectors';
import { selectIsIraAccount, selectTriageState } from '../triage/triage.selectors';

export const selectClientAccountsState = createSelector(
  fromFeature.getClientAccountsState,
  (state) => state,
);

export const selectClientAccountsEntity = createSelector(
  selectClientAccountsState,
  fromClientAccounts.getClientAccountsEntity,
);
export const selectClientAccountsLoaded = createSelector(
  selectClientAccountsState,
  fromClientAccounts.getClientAccountsLoaded,
);
export const selectClientAccountsLoading = createSelector(
  selectClientAccountsState,
  fromClientAccounts.getClientAccountsLoading,
);

export const selectDisplayedAccounts = createSelector(
  selectClientAccountsState,
  fromClientAccounts.getDisplayedAccounts,
);

export const selectEligibleJointAccounts = (isTransferTo: boolean = false) =>
  createSelector(
    selectClientAccountsState,
    selectTriageState,
    selectInternalGiftingManagedAccountsToggle,
    (clientAccountState, triageState, isInternalGiftingFromManagedEnabled) => {
      const showManagedAccounts =
        isInternalGiftingFromManagedEnabled && getConfigClientFromRTE().isInternal;
      return getIsGiftingFlow(triageState)
        ? fromClientAccounts.getEligibleJointAccounts(
            clientAccountState,
            isTransferTo,
            showManagedAccounts,
          )
        : fromClientAccounts.getEligibleWROSJointAccounts(clientAccountState, isTransferTo);
    },
  );

export const selectEligibleMinorAccounts = createSelector(
  selectClientAccountsState,
  fromClientAccounts.getEligibleMinorAccounts,
);

export const selectEligibleInternalTransferFromAccountsWithRejects = (
  rejectedAccounts: RejectedAccount[] = [],
) =>
  createSelector(
    selectClientAccountsState,
    selectInternalTransferFromAccountType,
    selectCashPlusInternalTransferToggle,
    (state, transferFromAccountType, isCashPlusEnabled) =>
      fromClientAccounts.getEligibleInternalTransferFromAccounts(
        state,
        fromClientAccounts.isSpecificAccountType(transferFromAccountType)
          ? transferFromAccountType
          : null,
        isCashPlusEnabled,
        rejectedAccounts,
      ),
  );

//IT Transfer-from Selector
export const selectEligibleInternalTransferFromAccounts =
  selectEligibleInternalTransferFromAccountsWithRejects();

export const selectEligibleInternalTransferToAccountsWithRejects = (
  rejectedAccounts: RejectedAccount[] = [],
) =>
  createSelector(
    selectClientAccountsState,
    selectInternalTransferToAccountType,
    selectInternalTransferFrom,
    selectCashPlusInternalTransferToggle,
    selectToFromConsolidationToggle,
    selectSelectedPositions,
    (
      state,
      transferToAccountType,
      transferFromAccount,
      isCashPlusEnabled,
      toFromConsolidationEnabled,
      selectedPositions,
    ) =>
      fromClientAccounts.getEligibleInternalTransferToAccounts(
        state,
        fromClientAccounts.isSpecificAccountType(transferToAccountType)
          ? transferToAccountType
          : null,
        transferFromAccount,
        isCashPlusEnabled,
        toFromConsolidationEnabled,
        selectedPositions,
        rejectedAccounts,
      ),
  );

//IT Transfer-to Selector
export const selectEligibleInternalTransferToAccounts =
  selectEligibleInternalTransferToAccountsWithRejects();

export const selectEligibleIndividualAccounts = (isTransferTo: boolean = true) =>
  createSelector(selectClientAccountsState, (clientAccountState) =>
    fromClientAccounts.getEligibleIndividualAccounts(clientAccountState, isTransferTo),
  );

export const selectEligibleTraditionalIRAAccounts = createSelector(
  selectClientAccountsState,
  fromClientAccounts.getEligibleTraditionalIRAAccounts,
);

export const selectEligibleRothIRAAccounts = createSelector(
  selectClientAccountsState,
  fromClientAccounts.getEligibleRothIRAAccounts,
);

export const selectEligibleClientAccount = (isTransferTo: boolean = false) =>
  createSelector(
    selectIsIraAccount,
    selectIraType,
    selectClientAccountsState,
    (isIraFlow, iraType, state) =>
      fromClientAccounts.getEligibleClientAccount(isIraFlow, iraType, state, isTransferTo),
  );

export const selectSpecificEligibleClientAccount = (
  isIRAAccount: boolean = false,
  iraType: IRATypes = IRATypes.UNKNOWN,
  isTransferTo: boolean = true,
) =>
  createSelector(selectClientAccountsState, (state) =>
    fromClientAccounts.getEligibleClientAccount(isIRAAccount, iraType, state, isTransferTo),
  );

export const selectEligibleGiftingAccounts = (accountType: AccountTypes) =>
  createSelector(
    selectClientAccountsState,
    selectInternalGiftingManagedAccountsToggle,
    (state, isInternalGiftingFromManagedEnabled) => {
      const showManagedAccounts =
        isInternalGiftingFromManagedEnabled && getConfigClientFromRTE().isInternal;
      switch (accountType) {
        case AccountTypes.INDV:
          return fromClientAccounts.getEligibleIndividualGiftingAccounts(
            state,
            showManagedAccounts,
          );
        case AccountTypes.TRUST:
          return fromClientAccounts.getEligibleTrustGiftingAccounts(state, showManagedAccounts);
        default:
          return { accounts: [] as FilteredAccount[] };
      }
    },
  );

export const selectVgClientPoid = createSelector(
  selectClientAccountsState,
  (state) => state.entity?.vgClientPoid,
);
